import React, { useEffect, useState } from 'react';
import { Shield, Plus } from 'lucide-react';
import { PartnerAuthService } from '../../../lib/services/partnerAuthService';
import { SectionToggle } from '../../shared/activity-drawer/ui/SectionToggle';
import { generatePassword } from './generatePassword';
import { PartnerPortalForm } from './PartnerPortalForm';
import { PartnerPortalAccessDetails } from './PartnerPortalAccessDetails';
import { isAccessLocked } from './partnerPortalAccessHelpers';
import { portalAccessKind } from './portalAccessKind';
import type { ExternalPartner, PartnerPortalAccess } from '../../../lib/types';

export interface PartnerPortalSectionProps {
  partner: ExternalPartner;
  open: boolean;
  onToggle(): void;
  reloadToken: number;
  onDataChanged(): void;
}

/**
 * Sezione "Portale partner" del tab Info partner, in stile Notion (mockup approvato,
 * colonna "Dopo"). Sostituisce PartnerPortalAccessManager.tsx mantenendone stati e azioni
 * (creazione, modifica, nuova password, disattiva/riattiva) ma senza box colorati: dati
 * come PropRow, azioni come righe fantasma (vedi PartnerPortalAccessDetails).
 */
export function PartnerPortalSection({ partner, open, onToggle, reloadToken, onDataChanged }: PartnerPortalSectionProps): JSX.Element {
  const [access, setAccess] = useState<PartnerPortalAccess | null>(null);
  const [checking, setChecking] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sessionPassword, setSessionPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiedField, setCopiedField] = useState<'credentials' | 'username' | null>(null);

  const loadAccess = async () => {
    setChecking(true);
    try {
      const list = await PartnerAuthService.getPartnerAccessList();
      // `collaborator_portal_access` è condivisa col portale progetti/collaboratore e non ha
      // un discriminatore: `external_partner_id` da solo può far combaciare la riga
      // dell'altra sezione (vedi portalAccessKind.ts). Filtriamo anche per kind === 'partner'
      // per non mostrare/gestire qui l'accesso "Portale progetti" dello stesso partner.
      // Fix vero: colonna `portal_type` dedicata (follow-up).
      setAccess(list.find((a) => a.external_partner_id === partner.id && portalAccessKind(a) === 'partner') || null);
    } catch (error) {
      console.error('Error loading partner portal access:', error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.id, reloadToken]);

  const flashCopied = (field: 'credentials' | 'username') => {
    setCopiedField(field);
    setTimeout(() => setCopiedField((prev) => (prev === field ? null : prev)), 1500);
  };

  const copyUsername = () => {
    if (!access) return;
    navigator.clipboard.writeText(access.username);
    flashCopied('username');
  };

  // "Nuova password" / "Sblocca e genera nuova password": stessa azione, resetPartnerPassword
  // (via portal_set_password) azzera già tentativi e blocco lato server, quindi sblocca da
  // sola quando serve. Cambia solo il testo di conferma mostrato all'utente.
  const handleNewPassword = async () => {
    if (!access) return;
    const locked = isAccessLocked(access);
    const confirmMessage = locked
      ? "Sbloccare l'account e generare una nuova password? Il partner verrà disconnesso."
      : 'Generare una nuova password? Il partner verrà disconnesso.';
    if (!confirm(confirmMessage)) return;
    setBusy(true);
    try {
      const password = generatePassword();
      const success = await PartnerAuthService.resetPartnerPassword(access.id, password);
      if (success) {
        setSessionPassword(password);
        navigator.clipboard.writeText(password);
        flashCopied('credentials');
        await loadAccess();
        onDataChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async () => {
    if (!access) return;
    setBusy(true);
    try {
      const ok = access.is_active
        ? await PartnerAuthService.disablePartnerAccess(access.id)
        : await PartnerAuthService.enablePartnerAccess(access.id);
      if (ok) {
        await loadAccess();
        onDataChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const locked = access ? isAccessLocked(access) : false;

  const summary = checking ? (
    <span className="min-w-0 truncate text-[12px] font-medium text-[#37352F] dark:text-white">…</span>
  ) : !access ? (
    <span className="min-w-0 truncate text-[12px] text-[rgba(55,53,47,0.45)] dark:text-white/40">Nessun accesso</span>
  ) : locked ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-[#37352F] dark:text-white">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#D44C47] dark:bg-[#E8827D]" />
      <span className="truncate">Bloccato</span>
    </span>
  ) : !access.is_active ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-[#37352F] dark:text-white">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#9B9A97]" />
      <span className="truncate">{access.username} · disattivato</span>
    </span>
  ) : (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-[#37352F] dark:text-white">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0F7B6C] dark:bg-[#4DB6A5]" />
      <span className="truncate">{access.username}</span>
    </span>
  );

  return (
    <>
      <SectionToggle
        open={open}
        onToggle={onToggle}
        icon={<Shield className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
        title="Portale partner"
        summary={summary}
        dataTour="partner-info-partner-portal"
      >
        {checking ? null : !access ? (
          <>
            <div className="px-1.5 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
              Il partner vede il catalogo servizi, richiede preventivi e consulta i report.
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowForm(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowForm(true); } }}
              className="flex items-center gap-1.5 px-1.5 py-1 rounded text-xs text-[#787774] dark:text-gray-400 cursor-pointer hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] hover:text-[#37352F] dark:hover:text-white"
            >
              <Plus className="h-3 w-3" strokeWidth={1.5} />
              Crea accesso
            </div>
          </>
        ) : (
          <PartnerPortalAccessDetails
            access={access}
            sessionPassword={sessionPassword}
            copiedField={copiedField}
            busy={busy}
            onCopyUsername={copyUsername}
            onCopySessionPassword={() => { if (sessionPassword) { navigator.clipboard.writeText(sessionPassword); flashCopied('credentials'); } }}
            onNewPassword={handleNewPassword}
            onToggleActive={handleToggleActive}
            onEdit={() => setShowForm(true)}
          />
        )}
      </SectionToggle>

      {showForm && (
        <PartnerPortalForm
          partner={partner}
          access={access}
          onClose={() => setShowForm(false)}
          onSaved={(password) => {
            setShowForm(false);
            if (password) setSessionPassword(password);
            // Come nella sezione "Portale progetti": senza forzare l'apertura qui, il
            // callout con la password appena generata non verrebbe mai renderizzato
            // (SectionToggle monta i children solo da aperto).
            if (!open) onToggle();
            loadAccess();
            onDataChanged();
          }}
        />
      )}
    </>
  );
}
